import { useState, type FormEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { ProductLockup } from '../components/brand/Brand'
import { Icon } from '../components/ui/Icon'
import { getSupabaseBrowserClient } from '../lib/supabase'
import { useAuth } from './AuthProvider'

function safeReturnTo(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/'
  return value
}

export function LoginExperiencePage() {
  const { session } = useAuth()
  const [searchParams] = useSearchParams()
  const returnTo = safeReturnTo(searchParams.get('returnTo'))
  const accessLinkUnavailable = searchParams.get('notice') === 'access-link-unavailable'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (session) return <Navigate to={returnTo} replace />

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    const supabase = getSupabaseBrowserClient()
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password })

    setSubmitting(false)
    if (signInError) setError('We could not sign you in with those details.')
  }

  return (
    <main className="login-experience">
      <section className="login-brand-panel">
        <ProductLockup inverse />
        <div className="login-brand-copy">
          <p className="eyebrow">Your AI governance framework</p>
          <h1>Know how AI is being used, governed and measured.</h1>
          <p>Swell brings AI use, strategy, roadmap and governance attention together so management can see what matters and act on it.</p>
        </div>
        <div className="login-capability-list" aria-label="Swell AI capabilities">
          <div><span aria-hidden="true"><Icon name="register" size={18} /></span><strong>Register AI use</strong></div>
          <div><span aria-hidden="true"><Icon name="register" size={18} /></span><strong>Govern every use</strong></div>
          <div><span aria-hidden="true"><Icon name="strategy" size={18} /></span><strong>Connect strategy and roadmap</strong></div>
        </div>
      </section>

      <section className="login-form-panel" aria-labelledby="login-title">
        <div className="login-form-card">
          <p className="eyebrow">Secure sign in</p>
          <h2 id="login-title">Sign in to Swell AI</h2>
          <p className="muted">First time here? Open the secure link in your access email to create your password.</p>
          {accessLinkUnavailable ? (
            <div className="inline-alert neutral">
              That secure access link can no longer be used. If your account is already set up, sign in below. Otherwise ask your organisation administrator for a new access email.
            </div>
          ) : null}
          {returnTo !== '/' ? <div className="inline-alert">After sign-in, Swell will return you to the governance task you opened.</div> : null}
          <form className="form-stack" onSubmit={(event) => { void submit(event) }}>
            <label>
              <span>Email</span>
              <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
            </label>
            <label>
              <span>Password</span>
              <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
            </label>
            {error ? <div className="inline-alert danger">{error}</div> : null}
            <button className="button primary login-submit" type="submit" disabled={submitting}>
              <span>{submitting ? 'Signing in…' : 'Sign in'}</span>
              {!submitting ? <Icon name="arrow-right" size={16} /> : null}
            </button>
          </form>
          <div className="auth-links"><Link to="/auth/forgot-password">Set or reset password</Link></div>
        </div>
      </section>
    </main>
  )
}
